import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth, API } from "../App";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import HeroBackground from "../components/HeroBackground";
import { 
  Search, 
  Home, 
  Building2, 
  MapPin, 
  BedDouble, 
  Bath, 
  Square, 
  ChevronRight,
  Star,
  Phone,
  Mail,
  ArrowRight,
  Menu,
  X,
  MessageCircle
} from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
} from "../components/ui/sheet";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import AIChatWidget from "../components/AIChatWidget";

const LandingPage = () => {
  const { user, login, logout } = useAuth();
  const navigate = useNavigate();
  const [featuredProperties, setFeaturedProperties] = useState([]);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [contactDialogOpen, setContactDialogOpen] = useState(false);
  const [businessSettings, setBusinessSettings] = useState({
    business_name: "Buyorent Real Estate Company Limited",
    email: "info@buyorent.com",
    phone: "+260 960911896",
    address: "69F Hilltop View",
    city: "Mufulira",
    state: "Copperbelt",
    country: "Zambia",
    office_hours: "Mon - Fri, 8:00 AM - 5:00 PM",
    about_us: "Your trusted partner in Buyorent Real Estate. Finding exceptional homes for exceptional people.",
    facebook_url: null,
    instagram_url: null,
    twitter_url: null,
    linkedin_url: null
  });

  // Search state
  const [searchCity, setSearchCity] = useState("");
  const [searchType, setSearchType] = useState("");
  const [listingType, setListingType] = useState("");

  useEffect(() => {
    fetchData();
    fetchBusinessSettings();
  }, []);

  const fetchBusinessSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings/business`);
      setBusinessSettings(prev => ({ ...prev, ...response.data }));
    } catch (error) {
      console.error("Error fetching business settings:", error);
    }
  };

  const fetchData = async () => {
    try {
      // Seed data first
      await axios.post(`${API}/seed`);
      
      const [propsRes, agentsRes] = await Promise.all([
        axios.get(`${API}/properties/featured?limit=6`),
        axios.get(`${API}/agents?limit=2`)
      ]);
      setFeaturedProperties(propsRes.data);
      setAgents(agentsRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    const params = new URLSearchParams();
    if (searchCity) params.append("city", searchCity);
    if (searchType) params.append("property_type", searchType);
    if (listingType) params.append("listing_type", listingType);
    navigate(`/properties?${params.toString()}`);
  };

  const formatPrice = (price, listingType) => {
    if (listingType === "rent") {
      return `K${price.toLocaleString()}/mo`;
    }
    return `K${price.toLocaleString()}`;
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass border-b border-border/40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2 active:scale-95 transition-transform">
              <img src={businessSettings.logo_url || "/logo.png"} alt="Buyorent" className="h-10 w-auto object-contain" />
              <span className="font-serif text-xl font-semibold tracking-tight">Buyorent</span>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-8">
              <Link to="/properties" className="text-sm font-medium hover:text-primary active:scale-95 transition-all" data-testid="nav-properties">
                Properties
              </Link>
              <Link to="/agents" className="text-sm font-medium hover:text-primary active:scale-95 transition-all" data-testid="nav-agents">
                Agents
              </Link>
              <Link to="/mortgage-calculator" className="text-sm font-medium hover:text-primary active:scale-95 transition-all" data-testid="nav-calculator">
                Calculator
              </Link>
              {user ? (
                <div className="flex items-center gap-4">
                  <Link to="/dashboard" className="text-sm font-medium hover:text-primary active:scale-95 transition-all" data-testid="nav-dashboard">
                    Dashboard
                  </Link>
                  <button
                    onClick={logout}
                    className="text-sm font-medium text-muted-foreground hover:text-foreground active:scale-95 transition-all"
                    data-testid="logout-btn"
                  >
                    Logout
                  </button>
                </div>
              ) : (
                <Button 
                  onClick={login} 
                  className="bg-primary text-primary-foreground hover:bg-primary/90 active:scale-95 h-10 px-6 rounded-sm transition-all"
                  data-testid="login-btn"
                >
                  Sign In
                </Button>
              )}
            </div>

            {/* Mobile Menu */}
            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild className="md:hidden">
                <button className="p-2" data-testid="mobile-menu-btn">
                  <Menu className="w-6 h-6" />
                </button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[280px]">
                <div className="flex flex-col gap-6 mt-8">
                  <Link 
                    to="/properties" 
                    className="text-lg font-medium"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Properties
                  </Link>
                  <Link 
                    to="/agents" 
                    className="text-lg font-medium"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Agents
                  </Link>
                  <Link 
                    to="/mortgage-calculator" 
                    className="text-lg font-medium"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Calculator
                  </Link>
                  {user ? (
                    <>
                      <Link 
                        to="/dashboard" 
                        className="text-lg font-medium"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        Dashboard
                      </Link>
                      <button 
                        onClick={() => { logout(); setMobileMenuOpen(false); }}
                        className="text-lg font-medium text-left text-muted-foreground"
                      >
                        Logout
                      </button>
                    </>
                  ) : (
                    <Button onClick={() => { login(); setMobileMenuOpen(false); }}>
                      Sign In
                    </Button>
                  )}
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </nav>

      {/* Hero Section - rotating gallery (random order, fade transition) */}
      <HeroBackground gallery={businessSettings?.homepage_gallery} />
      <section className="relative min-h-[90vh] flex items-center justify-center pt-16">
        <div className="absolute inset-0 bg-gradient-to-b from-emerald-900/30 via-emerald-900/50 to-emerald-900/80 pointer-events-none"></div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <p className="text-gold font-medium tracking-widest uppercase text-sm mb-4">
              Buyorent Real Estate Company Limited
            </p>
            <h1 className="text-4xl sm:text-5xl lg:text-7xl font-serif text-white leading-none mb-6">
              Find Your Dream
              <br />
              <span className="text-gold">Home</span>
            </h1>
            <p className="text-white/80 text-lg max-w-2xl mx-auto mb-12">
              Discover extraordinary properties in the most sought-after locations. 
              Your perfect home awaits.
            </p>
          </motion.div>

          {/* Search Box */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="bg-white/95 backdrop-blur-sm p-6 rounded-sm shadow-2xl max-w-4xl mx-auto"
          >
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-2 text-left">Location</label>
                <Input
                  placeholder="City or Zip"
                  value={searchCity}
                  onChange={(e) => setSearchCity(e.target.value)}
                  className="h-12 bg-background border-input rounded-sm"
                  data-testid="search-city-input"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-2 text-left">Property Type</label>
                <Select value={searchType} onValueChange={setSearchType}>
                  <SelectTrigger className="h-12 rounded-sm" data-testid="search-type-select">
                    <SelectValue placeholder="All Types" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    <SelectItem value="house">House</SelectItem>
                    <SelectItem value="apartment">Apartment</SelectItem>
                    <SelectItem value="condo">Condo</SelectItem>
                    <SelectItem value="land">Land</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-2 text-left">Listing Type</label>
                <Select value={listingType} onValueChange={setListingType}>
                  <SelectTrigger className="h-12 rounded-sm" data-testid="search-listing-select">
                    <SelectValue placeholder="Buy or Rent" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="sale">For Sale</SelectItem>
                    <SelectItem value="rent">For Rent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-end">
                <Button 
                  onClick={handleSearch}
                  className="w-full h-12 bg-primary text-primary-foreground hover:bg-primary/90 active:scale-95 rounded-sm font-medium transition-all"
                  data-testid="search-btn"
                >
                  <Search className="w-4 h-4 mr-2" />
                  Search
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Featured Properties */}
      <section className="py-24 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between mb-12">
            <div>
              <p className="text-gold font-medium tracking-widest uppercase text-sm mb-2">
                Featured Listings
              </p>
              <h2 className="text-3xl md:text-4xl font-serif">Exceptional Properties</h2>
            </div>
            <Link 
              to="/properties" 
              className="hidden md:flex items-center gap-2 text-sm font-medium text-primary hover:text-primary/80 transition-colors"
              data-testid="view-all-properties"
            >
              View All <ChevronRight className="w-4 h-4" />
            </Link>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3].map((i) => (
                <div key={i} className="bg-card rounded-sm overflow-hidden animate-pulse">
                  <div className="h-64 bg-muted"></div>
                  <div className="p-6">
                    <div className="h-4 bg-muted rounded w-3/4 mb-2"></div>
                    <div className="h-6 bg-muted rounded w-1/2 mb-4"></div>
                    <div className="h-4 bg-muted rounded w-full"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {featuredProperties.map((property, index) => (
                <motion.div
                  key={property.property_id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <Link 
                    to={`/properties/${property.property_id}`}
                    className="block property-card bg-card border border-border/40 rounded-sm overflow-hidden group"
                    data-testid={`property-card-${property.property_id}`}
                  >
                    <div className="relative h-64 overflow-hidden">
                      <img
                        src={property.images?.[0] || "https://images.unsplash.com/photo-1746458258536-b9ee5db20a73"}
                        alt={property.title}
                        className="w-full h-full object-cover property-image"
                      />
                      {/* Status badges */}
                      <div className="absolute top-4 left-4 flex gap-2">
                        <span className={`px-3 py-1 text-xs font-medium rounded-sm ${
                          property.listing_type === "rent" 
                            ? "bg-gold text-black" 
                            : "bg-primary text-white"
                        }`}>
                          {property.listing_type === "rent" ? "For Rent" : "For Sale"}
                        </span>
                        {/* Show RENTED or SOLD badge */}
                        {property.status === "rented" && (
                          <span className="px-3 py-1 text-xs font-medium rounded-sm bg-orange-500 text-white">
                            RENTED
                          </span>
                        )}
                        {property.status === "rented" && property.also_for_sale && (
                          <span className="px-3 py-1 text-xs font-medium rounded-sm bg-green-600 text-white">
                            FOR SALE
                          </span>
                        )}
                        {property.status === "sold" && (
                          <span className="px-3 py-1 text-xs font-medium rounded-sm bg-red-600 text-white">
                            SOLD
                          </span>
                        )}
                      </div>
                      {/* Overlay for rented/sold properties - only if NOT also for sale */}
                      {(property.status === "sold" || (property.status === "rented" && !property.also_for_sale)) && (
                        <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                          <span className="text-white text-2xl font-bold tracking-wider opacity-80">
                            {property.status === "rented" ? "RENTED" : "SOLD"}
                          </span>
                        </div>
                      )}
                      {/* Special overlay for rented + for sale */}
                      {property.status === "rented" && property.also_for_sale && (
                        <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent flex items-end justify-center pb-2">
                          <span className="text-white text-sm font-bold tracking-wider bg-green-600 px-2 py-1 rounded">
                            RENTED • FOR SALE
                          </span>
                        </div>
                      )}
                    </div>
                    <div className="p-6">
                      <div className="flex items-center gap-2 text-muted-foreground text-sm mb-2">
                        <MapPin className="w-4 h-4" />
                        {property.city}, {property.state}
                      </div>
                      <h3 className="font-serif text-xl mb-2 group-hover:text-primary transition-colors">
                        {property.title}
                      </h3>
                      <p className="font-mono text-2xl font-semibold text-primary mb-4">
                        {formatPrice(property.price, property.listing_type)}
                      </p>
                      <div className="flex items-center gap-4 text-sm text-muted-foreground border-t border-border pt-4">
                        <span className="flex items-center gap-1">
                          <BedDouble className="w-4 h-4" /> {property.bedrooms} Beds
                        </span>
                        <span className="flex items-center gap-1">
                          <Bath className="w-4 h-4" /> {property.bathrooms} Baths
                        </span>
                        <span className="flex items-center gap-1">
                          <Square className="w-4 h-4" /> {property.area_sqft.toLocaleString()} sqft
                        </span>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          )}

          <div className="mt-8 text-center md:hidden">
            <Link 
              to="/properties" 
              className="inline-flex items-center gap-2 text-sm font-medium text-primary"
            >
              View All Properties <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-24 bg-muted">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-gold font-medium tracking-widest uppercase text-sm mb-2">
              Our Services
            </p>
            <h2 className="text-3xl md:text-4xl font-serif">How We Help You</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                icon: Home,
                title: "Buy a Home",
                description: "Find your perfect home from our curated selection of luxury properties."
              },
              {
                icon: Building2,
                title: "Sell Property",
                description: "Get the best value for your property with our expert agents."
              },
              {
                icon: Search,
                title: "Property Management",
                description: "Full-service property management for investors and landlords."
              }
            ].map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="p-8 bg-card border border-border/50 rounded-sm hover:border-primary/30 transition-colors group"
              >
                <div className="w-14 h-14 bg-primary/10 rounded-sm flex items-center justify-center mb-6 group-hover:bg-primary/20 transition-colors">
                  <service.icon className="w-7 h-7 text-primary" />
                </div>
                <h3 className="font-serif text-xl mb-3">{service.title}</h3>
                <p className="text-muted-foreground leading-relaxed">{service.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Agents Section */}
      <section className="py-24 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between mb-12">
            <div>
              <p className="text-gold font-medium tracking-widest uppercase text-sm mb-2">
                Our Team
              </p>
              <h2 className="text-3xl md:text-4xl font-serif">Expert Agents</h2>
            </div>
            <Link 
              to="/agents" 
              className="hidden md:flex items-center gap-2 text-sm font-medium text-primary hover:text-primary/80 transition-colors"
              data-testid="view-all-agents"
            >
              Meet the Team <ChevronRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {agents.map((agent, index) => (
              <motion.div
                key={agent.agent_id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="flex gap-6 p-6 bg-card border border-border/40 rounded-sm group hover:border-primary/30 transition-colors"
                data-testid={`agent-card-${agent.agent_id}`}
              >
                <img
                  src={agent.photo}
                  alt={agent.name}
                  className="w-32 h-32 object-cover rounded-sm"
                />
                <div className="flex-1">
                  <h3 className="font-serif text-xl mb-1">{agent.name}</h3>
                  <div className="flex items-center gap-1 text-gold mb-3">
                    <Star className="w-4 h-4 fill-current" />
                    <span className="text-sm font-medium">{agent.rating}</span>
                    <span className="text-muted-foreground text-sm">• {agent.total_sales} sales</span>
                  </div>
                  <p className="text-muted-foreground text-sm mb-4 line-clamp-2">{agent.bio}</p>
                  <div className="flex gap-4">
                    <a href={`mailto:${agent.email}`} className="text-muted-foreground hover:text-primary transition-colors">
                      <Mail className="w-5 h-5" />
                    </a>
                    {agent.phone && (
                      <a href={`tel:${agent.phone}`} className="text-muted-foreground hover:text-primary transition-colors">
                        <Phone className="w-5 h-5" />
                      </a>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-primary text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-serif mb-4">Ready to Find Your Dream Home?</h2>
            <p className="text-white/80 max-w-2xl mx-auto mb-8">
              Let our expert agents guide you through the journey of finding the perfect property.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                onClick={() => navigate("/properties")}
                className="bg-white text-primary hover:bg-white/90 active:scale-95 h-12 px-8 rounded-sm font-medium transition-all"
                data-testid="cta-browse-btn"
              >
                Browse Properties <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <Button 
                onClick={() => setContactDialogOpen(true)}
                variant="outline"
                className="border-white text-white hover:bg-white/10 active:scale-95 h-12 px-8 rounded-sm font-medium transition-all"
                data-testid="cta-contact-btn"
              >
                <Phone className="w-4 h-4 mr-2" />
                Contact Us
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-16 bg-foreground text-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <img src={businessSettings.logo_url || "/logo.png"} alt="Buyorent" className="h-10 w-auto object-contain" />
                <span className="font-serif text-xl font-semibold">Buyorent</span>
              </div>
              <p className="text-muted-foreground text-sm">
                Your trusted partner in Buyorent Real Estate. Finding exceptional homes for exceptional people.
              </p>
            </div>
            <div>
              <h4 className="font-medium mb-4">Quick Links</h4>
              <div className="flex flex-col gap-2 text-sm text-muted-foreground">
                <Link to="/properties" className="hover:text-background transition-colors">Properties</Link>
                <Link to="/agents" className="hover:text-background transition-colors">Agents</Link>
                <Link to="/mortgage-calculator" className="hover:text-background transition-colors">Calculator</Link>
              </div>
            </div>
            <div>
              <h4 className="font-medium mb-4">Property Types</h4>
              <div className="flex flex-col gap-2 text-sm text-muted-foreground">
                <Link to="/properties?property_type=house" className="hover:text-background transition-colors">Houses</Link>
                <Link to="/properties?property_type=apartment" className="hover:text-background transition-colors">Apartments</Link>
                <Link to="/properties?property_type=condo" className="hover:text-background transition-colors">Condos</Link>
                <Link to="/properties?property_type=land" className="hover:text-background transition-colors">Land</Link>
              </div>
            </div>
            <div>
              <h4 className="font-medium mb-4">Contact</h4>
              <div className="flex flex-col gap-2 text-sm text-muted-foreground">
                <p>{businessSettings.address}</p>
                <p>{businessSettings.city}, {businessSettings.state}</p>
                <p>{businessSettings.country}</p>
                <p>{businessSettings.email}</p>
                <p>{businessSettings.phone}</p>
              </div>
            </div>
          </div>
          <div className="border-t border-muted-foreground/20 mt-12 pt-8 text-center text-sm text-muted-foreground">
            © 2025 {businessSettings.business_name}. All rights reserved.
          </div>
        </div>
      </footer>

      {/* AI Chat Widget */}
      <AIChatWidget isOpen={chatOpen} setIsOpen={setChatOpen} />

      {/* Contact Dialog */}
      <Dialog open={contactDialogOpen} onOpenChange={setContactDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-center">Contact {businessSettings.business_name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-6 py-4">
            <p className="text-center text-muted-foreground">
              Get in touch with our team. We're here to help you find your dream property.
            </p>
            
            <div className="space-y-4">
              {/* Phone */}
              <a 
                href={`tel:${businessSettings.phone?.replace(/\s/g, '')}`}
                className="flex items-center gap-4 p-4 bg-primary/5 rounded-sm hover:bg-primary/10 transition-colors group"
              >
                <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center">
                  <Phone className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="font-medium group-hover:text-primary transition-colors">Call Us</p>
                  <p className="text-lg font-semibold text-primary">{businessSettings.phone}</p>
                </div>
              </a>
              
              {/* Email */}
              <a 
                href={`mailto:${businessSettings.email}`}
                className="flex items-center gap-4 p-4 bg-primary/5 rounded-sm hover:bg-primary/10 transition-colors group"
              >
                <div className="w-12 h-12 bg-gold rounded-full flex items-center justify-center">
                  <Mail className="w-5 h-5 text-black" />
                </div>
                <div>
                  <p className="font-medium group-hover:text-primary transition-colors">Email Us</p>
                  <p className="text-lg font-semibold text-primary">{businessSettings.email}</p>
                </div>
              </a>
              
              {/* Address */}
              <div className="flex items-center gap-4 p-4 bg-muted/50 rounded-sm">
                <div className="w-12 h-12 bg-muted rounded-full flex items-center justify-center">
                  <MapPin className="w-5 h-5 text-muted-foreground" />
                </div>
                <div>
                  <p className="font-medium">Visit Our Office</p>
                  <p className="text-sm text-muted-foreground">{businessSettings.address}</p>
                  <p className="text-sm text-muted-foreground">{businessSettings.city}, {businessSettings.state}, {businessSettings.country}</p>
                </div>
              </div>
            </div>
            
            <p className="text-center text-xs text-muted-foreground">
              Office Hours: {businessSettings.office_hours}
            </p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Chat FAB */}
      <button
        onClick={() => setChatOpen(true)}
        className="fixed bottom-24 right-6 w-14 h-14 bg-primary text-white rounded-full shadow-lg hover:bg-primary/90 active:scale-90 transition-all hover:scale-105 z-[60]"
        data-testid="chat-fab"
      >
        <MessageCircle className="w-6 h-6 mx-auto" />
      </button>
    </div>
  );
};

export default LandingPage;
